import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Eye, User, Tag, Star } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { CommentSection, RatingStars } from '@/components/blog';
import { Button } from '@/components/ui/button';
import { useBlogPost, useUserRating, useRatePost } from '@/hooks/useBlog';
import { useAuthStore } from '@/stores/auth.store';
import { useState } from 'react';

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const { data: post, isLoading, error } = useBlogPost(slug || '');
  const { data: userRating } = useUserRating(post?.id || '');
  const ratePost = useRatePost(post?.id || '');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="mx-auto max-w-3xl animate-pulse space-y-6">
            <div className="h-8 w-3/4 bg-muted rounded" />
            <div className="h-4 w-1/2 bg-muted rounded" />
            <div className="aspect-video bg-muted rounded-xl" />
            <div className="space-y-3">
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-5/6 bg-muted rounded" />
              <div className="h-4 w-4/6 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('blogPage.postNotFound')}</h2>
          <p className="text-muted-foreground mb-6">{t('blogPage.postNotFoundDescription')}</p>
          <Link to="/blog">
            <Button>{t('blogPage.backToBlog')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-3xl">
          {/* Back */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('blogPage.backToBlog')}
          </Link>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Category & Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
                {post.category?.name || t('blogPage.uncategorized')}
              </span>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {new Date(post.createdAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Eye className="h-4 w-4" />
                {post.viewCount} {t('blogPage.views')}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>

            {/* Author */}
            <div className="flex items-center gap-3 mb-8 pb-6 border-b">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                <User className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium">{post.authorName}</p>
                <p className="text-xs text-muted-foreground">
                  {t('blogPage.updatedAt')} {new Date(post.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Cover Image */}
            {post.coverImage && (
              <div className="mb-8 overflow-hidden rounded-xl">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div
              className="prose prose-emerald max-w-none mb-8"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Image Gallery */}
            {post.images && post.images.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-3">{t('blogPage.gallery')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {post.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(img)}
                      className="overflow-hidden rounded-lg aspect-video hover:opacity-90 transition-opacity"
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Image Lightbox */}
            {selectedImage && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                onClick={() => setSelectedImage(null)}
              >
                <img
                  src={selectedImage}
                  alt=""
                  className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                />
              </div>
            )}

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Rating */}
            <div className="mb-8 pb-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Star className="h-5 w-5 text-emerald-500" />
                    {t('blogPage.rating')}
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    {Number(post.averageRating ?? 0).toFixed(1)} ({post.ratingCount ?? 0} {t('blogPage.ratings')})
                  </span>
                </div>
                {isAuthenticated && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t('blogPage.yourRating')}:</span>
                    <RatingStars
                      rating={userRating?.rating || 0}
                      onRate={(rating) => ratePost.mutate(rating)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Comments */}
            <CommentSection postId={post.id} />
          </motion.article>
        </div>
      </div>
    </div>
  );
}
